/**
 * useOrgManagementScope Hook
 * V137: 조직관리 데이터 조회 스코프
 *
 * 백엔드 getOrgManagementScope 함수를 호출하여
 * 현재 사용자가 조회 가능한 조직 ID 목록을 가져옵니다.
 *
 * **스코프 규칙:**
 * - MEMBER / SPECIAL / TEAM_LEADER: 본인 orgUnit만 (SELF)
 * - DIRECTOR: 본인 orgUnit + 하위 전체 (SUBTREE)
 * - HQ_HEAD: 본인 orgUnit + 하위 전체 (SUBTREE)
 * - CEO: 전체 orgUnits (ALL)
 *
 * **사용 예시:**
 * ```tsx
 * function OrgMembersPage() {
 *   const { scopeOrgUnitIds, scopeMode, isLoading, error } = useOrgManagementScope();
 *
 *   // 스코프 기반 데이터 조회
 *   const { employees } = useEmployees({
 *     orgUnitIds: scopeOrgUnitIds,
 *   });
 *
 *   return (
 *     <div>
 *       <p>조회 가능 조직 수: {scopeOrgUnitIds.length}</p>
 *       <p>스코프 모드: {scopeMode}</p>
 *       {employees.map(...)}
 *     </div>
 *   );
 * }
 * ```
 */

import { useState, useEffect, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';

/**
 * 직책 레벨 (V136.1 동일)
 */
export type JobTitleLevel =
  | 'CEO'
  | 'HQ_HEAD'
  | 'DIRECTOR'
  | 'TEAM_LEADER'
  | 'SPECIAL'
  | 'MEMBER';

/**
 * 스코프 모드
 */
export type ScopeMode = 'SELF' | 'SUBTREE' | 'ALL';

/**
 * 요청자 정보
 */
export interface ScopeRequester {
  uid: string;
  orgUnitId: string | null;
  orgUnitName: string | null;
  jobTitleId: string | null;
  jobTitleCode: string | null;
  jobTitleLevel: JobTitleLevel;
}

/**
 * useOrgManagementScope 반환 타입
 */
export interface UseOrgManagementScopeResult {
  /** 로딩 중 여부 */
  isLoading: boolean;

  /** 에러 */
  error: Error | null;

  /** 조회 가능한 조직 ID 목록 */
  scopeOrgUnitIds: string[];

  /** 스코프 루트 조직 ID (ALL인 경우 null) */
  scopeRootOrgUnitId: string | null;

  /** 스코프 모드 */
  scopeMode: ScopeMode | null;

  /** 요청자 정보 */
  requester: ScopeRequester | null;

  /** 스코프 다시 조회 */
  refetch: () => void;

  /** 특정 조직이 스코프 내인지 확인 */
  isInScope: (orgUnitId: string) => boolean;

  /** V146_DEBUG: 백엔드 디버그 정보 */
  debugMeta: ScopeDebugMeta | null;

  // V146_DEBUG: 호출 상태 추적용
  /** 호출 상태: 'loading' | 'ok' | 'error' */
  callStatus: 'loading' | 'ok' | 'error';
  /** 에러 메시지 (있으면) */
  errorMessage: string | null;
  /** 원본 응답 전체 (디버그용) */
  rawResponse: any | null;
}

/**
 * V146_DEBUG: 디버그 메타 정보
 */
export interface ScopeDebugMeta {
  v146_debug: boolean;
  uid: string;
  isSystemAdmin: boolean;
  isRoleManager: boolean;
  isRoleExec: boolean;
  isRoleHr: boolean;
  assignedRoleIds: string[];
  assignedRoleIdsCount: number;
  roleAssignmentsSnapshotSize: number;
  userJobTitleCode: string | null;
  userJobTitleLevel: JobTitleLevel;
  userOrgUnitId: string | null;
  scopeMode: ScopeMode;
  scopeOrgUnitIdsCount: number;
}

/**
 * 백엔드 응답 타입
 */
interface GetOrgManagementScopeResponse {
  success: boolean;
  requester: ScopeRequester;
  scopeOrgUnitIds: string[];
  scopeRootOrgUnitId: string | null;
  scopeMode: ScopeMode;
  message?: string;
  // V146_DEBUG: 디버그 정보
  debugMeta?: ScopeDebugMeta;
}

/**
 * 모듈 레벨 캐시
 * 동일 세션 내에서 스코프 재사용
 */
let scopeCache: {
  requester: ScopeRequester;
  scopeOrgUnitIds: string[];
  scopeRootOrgUnitId: string | null;
  scopeMode: ScopeMode;
  timestamp: number;
} | null = null;

const CACHE_TTL_MS = 5 * 60 * 1000; // 5분

/**
 * useOrgManagementScope Hook
 */
export function useOrgManagementScope(): UseOrgManagementScopeResult {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [scopeOrgUnitIds, setScopeOrgUnitIds] = useState<string[]>([]);
  const [scopeRootOrgUnitId, setScopeRootOrgUnitId] = useState<string | null>(null);
  const [scopeMode, setScopeMode] = useState<ScopeMode | null>(null);
  const [requester, setRequester] = useState<ScopeRequester | null>(null);
  // V146_DEBUG: 디버그 메타 정보
  const [debugMeta, setDebugMeta] = useState<ScopeDebugMeta | null>(null);

  // V146_DEBUG: 호출 상태 추적
  const [callStatus, setCallStatus] = useState<'loading' | 'ok' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [rawResponse, setRawResponse] = useState<any | null>(null);

  // 중복 fetch 방지
  const isFetching = useRef(false);

  const fetchScope = async () => {
    // 중복 요청 방지
    if (isFetching.current) return;

    // 캐시가 유효한 경우
    if (scopeCache && (Date.now() - scopeCache.timestamp) < CACHE_TTL_MS) {
      setRequester(scopeCache.requester);
      setScopeOrgUnitIds(scopeCache.scopeOrgUnitIds);
      setScopeRootOrgUnitId(scopeCache.scopeRootOrgUnitId);
      setScopeMode(scopeCache.scopeMode);
      setIsLoading(false);
      return;
    }

    // Firebase가 초기화되지 않은 경우 (DEMO 모드)
    if (!functions) {
      setScopeOrgUnitIds([]);
      setScopeMode(null);
      setIsLoading(false);
      return;
    }

    try {
      isFetching.current = true;
      setIsLoading(true);
      setError(null);
      setCallStatus('loading');
      setErrorMessage(null);

      const getOrgManagementScope = httpsCallable<
        Record<string, never>,
        GetOrgManagementScopeResponse
      >(functions!, 'getOrgManagementScope');

      const result = await getOrgManagementScope({});

      // V146_DEBUG: 원본 응답 저장
      setRawResponse(result.data);

      if (result.data.success) {
        // 상태 업데이트
        setRequester(result.data.requester);
        setScopeOrgUnitIds(result.data.scopeOrgUnitIds);
        setScopeRootOrgUnitId(result.data.scopeRootOrgUnitId);
        setScopeMode(result.data.scopeMode);
        // V146_DEBUG: 디버그 메타 저장
        setDebugMeta(result.data.debugMeta || null);
        // V146_DEBUG: 호출 성공
        setCallStatus('ok');

        // 캐시 저장
        scopeCache = {
          requester: result.data.requester,
          scopeOrgUnitIds: result.data.scopeOrgUnitIds,
          scopeRootOrgUnitId: result.data.scopeRootOrgUnitId,
          scopeMode: result.data.scopeMode,
          timestamp: Date.now(),
        };

      } else {
        const msg = result.data.message || '스코프 조회 실패 (success=false)';
        setCallStatus('error');
        setErrorMessage(msg);
        throw new Error(msg);
      }
    } catch (err: any) {
      const errMsg = err?.message || err?.code || String(err);
      console.error('[useOrgManagementScope] V146_DEBUG 스코프 조회 실패:', errMsg, err);
      setError(err);
      setCallStatus('error');
      setErrorMessage(errMsg);
      setScopeOrgUnitIds([]);
      setScopeMode(null);
    } finally {
      setIsLoading(false);
      isFetching.current = false;
    }
  };

  // 컴포넌트 마운트 시 스코프 조회
  useEffect(() => {
    fetchScope();
  }, []);

  // 특정 조직이 스코프 내인지 확인
  const isInScope = (orgUnitId: string): boolean => {
    return scopeOrgUnitIds.includes(orgUnitId);
  };

  // 스코프 다시 조회 (캐시 무효화)
  const refetch = () => {
    scopeCache = null;
    fetchScope();
  };

  return {
    isLoading,
    error,
    scopeOrgUnitIds,
    scopeRootOrgUnitId,
    scopeMode,
    requester,
    refetch,
    isInScope,
    // V146_DEBUG: 디버그 메타 정보
    debugMeta,
    // V146_DEBUG: 호출 상태 추적
    callStatus,
    errorMessage,
    rawResponse,
  };
}

/**
 * 스코프 캐시 무효화 함수
 * 조직 변경 등으로 스코프가 변경될 수 있을 때 호출
 */
export function invalidateOrgManagementScopeCache(): void {
  scopeCache = null;
}
